import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { Lead } from '../../types';

interface KanbanBoardProps {
  leads: Lead[];
  onStatusChange: (leadId: string, newStatus: string) => void;
  onLeadClick: (lead: Lead) => void;
}

const COLUMNS = [
  { status: 'new', label: 'New', color: 'bg-blue-500', bgColor: 'bg-blue-50' },
  { status: 'contacted', label: 'Contacted', color: 'bg-yellow-500', bgColor: 'bg-yellow-50' },
  { status: 'qualified', label: 'Qualified', color: 'bg-purple-500', bgColor: 'bg-purple-50' },
  { status: 'converted', label: 'Converted', color: 'bg-green-500', bgColor: 'bg-green-50' },
  { status: 'lost', label: 'Lost', color: 'bg-red-500', bgColor: 'bg-red-50' },
];

const sourceIcons: Record<string, string> = {
  website: 'W',
  'Contact Form': 'CF',
  'Quick Contact': 'QC',
  'Booking Request': 'BR',
  'Zip Funnel': 'ZF',
  manual: 'M',
  referral: 'R',
  social: 'S',
};

const sourceColors: Record<string, string> = {
  website: 'bg-blue-100 text-blue-700',
  'Contact Form': 'bg-indigo-100 text-indigo-700',
  'Quick Contact': 'bg-cyan-100 text-cyan-700',
  'Booking Request': 'bg-amber-100 text-amber-700',
  'Zip Funnel': 'bg-emerald-100 text-emerald-700',
  manual: 'bg-zinc-100 text-zinc-700',
  referral: 'bg-purple-100 text-purple-700',
  social: 'bg-pink-100 text-pink-700',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ leads, onStatusChange, onLeadClick }) => {
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);

  const columnLeads = COLUMNS.map((col) => ({
    ...col,
    leads: leads.filter((l) => l.status === col.status),
  }));

  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    e.dataTransfer.setData('text/plain', leadId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedLeadId(leadId);
  };

  const handleDragOver = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(status);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('text/plain');
    setDragOverColumn(null);
    setDraggedLeadId(null);

    const lead = leads.find((l) => l.id === leadId);
    if (lead && lead.status !== targetStatus) {
      onStatusChange(leadId, targetStatus);
    }
  };

  const handleDragEnd = () => {
    setDragOverColumn(null);
    setDraggedLeadId(null);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
      {columnLeads.map((col) => (
        <div
          key={col.status}
          className={`flex-shrink-0 w-64 rounded-xl transition-colors ${
            dragOverColumn === col.status
              ? 'bg-accent/10 ring-2 ring-accent/30'
              : col.bgColor
          }`}
          onDragOver={(e) => handleDragOver(e, col.status)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, col.status)}
        >
          {/* Column Header */}
          <div className="px-3 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${col.color}`} />
              <span className="text-sm font-semibold text-primary">{col.label}</span>
            </div>
            <span className="text-xs font-medium text-muted bg-white rounded-full px-2 py-0.5">
              {col.leads.length}
            </span>
          </div>

          {/* Cards */}
          <div className="px-2 pb-2 space-y-2 min-h-[100px]">
            {col.leads.map((lead, index) => (
              <motion.div
                key={lead.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: draggedLeadId === lead.id ? 0.5 : 1,
                  y: 0,
                }}
                transition={{ delay: index * 0.03, duration: 0.2 }}
                draggable
                onDragStart={(e) => handleDragStart(e as unknown as React.DragEvent, lead.id)}
                onDragEnd={handleDragEnd}
                onClick={() => onLeadClick(lead)}
                className="bg-white border border-zinc-200 rounded-lg p-3 cursor-pointer hover:shadow-md transition-all select-none"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-sm font-medium text-primary truncate">{lead.name}</p>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${sourceColors[lead.source] || sourceColors.manual}`}>
                    {sourceIcons[lead.source] || lead.source.charAt(0).toUpperCase()}
                  </span>
                </div>

                {(lead.phone || lead.email) && (
                  <div className="space-y-1 mb-2">
                    {lead.phone && (
                      <p className="text-xs text-muted truncate flex items-center gap-1">
                        <PhoneSmallIcon />
                        {lead.phone}
                      </p>
                    )}
                    {lead.email && (
                      <p className="text-xs text-muted truncate flex items-center gap-1">
                        <EmailSmallIcon />
                        {lead.email}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-[11px] text-zinc-400">
                  {timeAgo(lead.createdAt)}
                </p>
              </motion.div>
            ))}

            {col.leads.length === 0 && (
              <div className="flex items-center justify-center h-20 text-xs text-zinc-400 border-2 border-dashed border-zinc-200 rounded-lg">
                Drop here
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const PhoneSmallIcon: React.FC = () => (
  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);

const EmailSmallIcon: React.FC = () => (
  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
  </svg>
);

export default KanbanBoard;
